import React from "react";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import MHeader from "@/components/ui/organisms/MHeader";
import {
  IconStarFilled,
  IconLockFilled,
  IconCloudDownload,
  IconTrash,
  IconFileSearch,
  IconEdit,
  IconCircleX,
  IconReload,
} from "@tabler/icons-react";
import { getMemberInfo } from "@/hooks/useMembers";
import { useDocument, buildYearFilter } from "@/hooks/useDocument";
import { useDocumentSettings } from "@/hooks/useDocumentSettings";
import HealthMemoryCard from "@/components/ui/atoms/HealthMemoryCard";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import { PAGE_SIZE } from "@/utils/constants";
import styles from "./document.module.css";
import { formatDocumentDateShort } from "@/utils/date-utils";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { FileCategory, getFileCategoryLabel } from "@/types/document.dto";
import MChip from "@/components/ui/atoms/MChip";
import MDocumentCard from "@/components/ui/atoms/MDocumentCard";
import MFilter from "@/components/ui/atoms/MFilter";
import { MButton } from "@/components/ui/atoms/MButtons";
import DocumentDrawer from "./DocumentDrawer";
import DocumentCommentDrawer from "./DocumentCommentDrawer";
import MLoader from "@/components/ui/atoms/MLoader";
import MDialog from "@/components/ui/atoms/MDialog";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { useHealthMemory } from "@/hooks/useHealthMemory";
import MDocumentUploadOptions from "@/components/ui/molecules/MDocumentUploadOptions";
import { t } from "i18next";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { useScrollRestore } from "@/hooks/useScrollRestore";

// Fixed display order for the "Filter by type" drawer, matching the design spec
// (Prescription, Procedure, Report, Bill, Identity, Insurance) — do not rely on
// FileCategory's numeric enum order, which doesn't match this sequence.
const CATEGORY_FILTER_ORDER = [
  FileCategory.Prescription,
  FileCategory.DischargeSummary,
  FileCategory.LabReport,
  FileCategory.MedicalBill,
  FileCategory.Identity,
  FileCategory.InsuranceCard,
];

// Health memories aren't a FileCategory (they're a separate collection merged
// in server-side via `skipMemories: false`), so they're represented in the
// "type" filter's selectedCategories array with a sentinel that can't collide
// with a real FileCategory value.
const HEALTH_MEMORY_FILTER_VALUE = -1;

const DocumentListing: React.FC = () => {
  // ...existing hooks and state
  const toast = useToast();
  const [commentLoading, setCommentLoading] = React.useState(false);
  const [healthMemoryToDelete, setHealthMemoryToDelete] = React.useState<
    any | null
  >(null);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showUploadOptions, setShowUploadOptions] = React.useState(false);

  function handleHealthMemoryDelete(memory: any) {
    setHealthMemoryToDelete(memory);
    setShowDeleteDialog(true);
  }
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";

  // Handle back navigation
  const handleBack = () => {
    localStorage.removeItem(`documentFilters_${memberId}`);
    const backUrl = (location.state as any)?.backUrl;
    if (backUrl) {
      navigate(backUrl);
      return;
    }

    // DocumentsNavigation is obsolete — fall back to the Documents landing page.
    navigate("/documents");
  };

  const {
    fetchDocuments,
    downloadDocument,
    updateDocumentFlag,
    deleteDocument,
    updateDocumentNotes,
    bulkUpdateFlag,
    bulkDelete,
    vectorSearchDocuments,
  } = useDocument(memberId);
  const { getSettings } = useDocumentSettings(memberId);
  const [documents, setDocuments] = React.useState<any[]>([]);
  const [allDocuments, setAllDocuments] = React.useState<any[]>([]);
  const { healthMemories, error, fetchHealthMemories, deleteHealthMemory } =
    useHealthMemory(memberId);

  // Helper function to fetch and merge document settings
  const fetchAndMergeSettings = async (data: any[]) => {
    if (data.length === 0) return data;

    // Filter only documents (exclude health memories)
    const documentsOnly = data.filter((item) => item.type !== "healthMemory");
    if (documentsOnly.length === 0) return data;

    try {
      // Fetch settings for each document
      const settingsPromises = documentsOnly.map((doc) =>
        getSettings(doc._id || doc.id).catch(() => null)
      );
      const settingsResults = await Promise.all(settingsPromises);

      // Create a map of documentId -> settings
      const settingsMap = new Map();
      documentsOnly.forEach((doc, index) => {
        const docId = doc._id || doc.id;
        settingsMap.set(docId, settingsResults[index]);
      });

      // Merge settings with documents, leave health memories unchanged
      return data.map((item) => {
        if (item.type === "healthMemory") {
          return item; // Health memories don't have settings
        }

        const docId = item._id || item.id;
        const docSettings = settingsMap.get(docId);

        return {
          ...item,
          isImportant: docSettings?.isImportant || false,
          availableOffline: docSettings?.availableOffline || false,
        };
      });
    } catch (error) {
      console.error("Error fetching document settings:", error);
      // Return data with default settings for documents
      return data.map((item) => {
        if (item.type === "healthMemory") {
          return item;
        }
        return {
          ...item,
          isImportant: false,
          availableOffline: false,
        };
      });
    }
  };

  // Refetch with whatever filters are currently active, without resetting scroll.
  // Used after in-place edits (category change, owner change) so the list
  // reflects the change (doc may disappear if it no longer matches the filter)
  // without jumping to the top.
  async function refetchWithCurrentFilters() {
    try {
      const filters: any = {};
      if (categoryFilter) filters.category = categoryFilter;
      const yearFilter = buildYearFilter(selectedYears);
      if (yearFilter) filters.documentDate = yearFilter;
      filters.memberId = {
        operation: "EQUAL",
        value: memberId,
        type: "STRING",
      };

      const pageSize = Math.max(documents.length, PAGE_SIZE);
      const res = (await fetchDocuments({
        filters,
        sort: [{ key: "documentDate", direction: "desc" }],
        skipMemories: !includeHealthMemories,
        page: 1,
        pageSize,
      })) as { data: any[]; total?: number };
      const data = Array.isArray(res?.data) ? res.data : [];
      const dataWithSettings = await fetchAndMergeSettings(data);
      setDocuments(dataWithSettings);
      setAllDocuments(dataWithSettings);
      setTotalCount(res.total || data.length);
    } catch (err) {
      console.error("Failed to refetch after document update", err);
    }
  }

  function clearFiltersAndRefetch(clearFilters: boolean = true) {
    if (clearFilters) {
      fetchDocuments({
        filters: {},
        sort: [{ key: "documentDate", direction: "desc" }],
      }).then(async (res: any) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        const dataWithSettings = await fetchAndMergeSettings(data);
        setDocuments(dataWithSettings);
        setAllDocuments(dataWithSettings);
        searchParams.delete("category");
        searchParams.delete("year");
        searchParams.delete("search");
        setSearchParams(searchParams, { replace: true });
        setSelectedCategories([]);
        setSelectedYears([]);
        setSearchTerm("");
        setSearchInputValue("");
      });
    } else {
      refetchWithCurrentFilters();
    }
  }

  // Minimal search UX state
  const [searchInputValue, setSearchInputValue] = React.useState("");
  const handleSearch = (value: string) => {
    setSearchInputValue(value);
    setSearchTerm(value);
    if (value.length >= 1) {
      searchParams.set("search", value);
      setSearchParams(searchParams, { replace: true });
    } else {
      searchParams.delete("search");
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Only show processed and OCR-readable documents, just like DocumentsLanding
  const [page, setPage] = React.useState(2); // next page to fetch
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalCount, setTotalCount] = React.useState(0);
  const processedDocuments = documents.filter(
    (doc) => doc.type === "healthMemory" || doc.status === 2
  );

  function clearSearch() {
    setSearchTerm("");
    setSearchInputValue("");

    // Only remove the 'search' param, keep others (category, year)
    searchParams.delete("search");
    setSearchParams(searchParams, { replace: true });

    // Reset displayed documents to cached allDocuments
    setDocuments(allDocuments);
  }

  // Clears category, year, and search filters — shared by the "Reset" link
  // and the search box's embedded filter-badge close button.
  function handleResetFilters() {
    searchParams.delete("category");
    searchParams.delete("year");
    searchParams.delete("search");
    setSearchParams(searchParams, { replace: true });
    setSelectedCategories([]);
    setSelectedYears([]);
    setSearchTerm("");
    setSearchInputValue("");
  }

  const [searchParams, setSearchParams] = useSearchParams();

  // Scoped per-member so filters never bleed across profile switches.
  const FILTER_STORAGE_KEY = `documentFilters_${memberId}`;

  // Initialise searchTerm synchronously from the URL or the persisted filter
  // store on first render. Two reasons:
  //   1. The standard back-nav (useBackNavigation) strips the URL so a
  //      back-navigation into this screen loses ?search=… in the URL.
  //   2. If we initialise to "" and then patch later via effects, the data
  //      effect fires once with searchTerm="" → runs the non-search fetch
  //      → scroll-restore fires against that wrong list and marks itself
  //      done. The subsequent fuzzy fetch then can't trigger restoration.
  //   Reading both URL and localStorage upfront avoids the race entirely.
  const [searchTerm, setSearchTerm] = React.useState<string>(() => {
    const fromUrl = searchParams.get("search");
    if (fromUrl && fromUrl.trim()) return fromUrl;
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.search && typeof parsed.search === "string") {
          return parsed.search;
        }
      }
    } catch {
      // localStorage unavailable / corrupted — fall back to empty.
    }
    return "";
  });

  const [debounceTimer, setDebounceTimer] =
    React.useState<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = React.useState(true);
  const { saveScroll, saveItemCount, getSavedItemCount } = useScrollRestore(
    "documentListing",
    loading
  );
  const isSearchMode = searchTerm.length >= 3;
  const [drawerMode, setDrawerMode] = React.useState<
    "none" | "document" | "comment"
  >("none");
  const [selectedDoc, setSelectedDoc] = React.useState<any | null>(null);

  // Select mode state
  const [selectMode, setSelectMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = React.useState(false);

  const categoryParam = searchParams.get("category");
  const [typeDrawerOpen, setTypeDrawerOpen] = React.useState(false);
  const [selectedCategories, setSelectedCategories] = React.useState<number[]>(
    () => {
      if (!categoryParam) return [];
      return categoryParam
        .split(",")
        .map(Number)
        .filter((v) => !isNaN(v));
    }
  );

  // Real FileCategory values only (strips the "Health memory" sentinel) — use
  // this, not selectedCategories, whenever building the backend `category` filter.
  const realSelectedCategories = selectedCategories.filter(
    (c) => c !== HEALTH_MEMORY_FILTER_VALUE
  );
  // Health memories are included when explicitly selected as a type filter, or
  // when no type filter is active at all (default "all records" view).
  const includeHealthMemories =
    selectedCategories.length === 0 ||
    selectedCategories.includes(HEALTH_MEMORY_FILTER_VALUE);

  // The `category` filter to send with document fetches. `undefined` means "no
  // restriction" (default, unfiltered view). When only "Health memory" is
  // selected (no real FileCategory chosen), filters to an impossible category
  // ($in: []) so the merge endpoint returns zero regular documents and fills
  // the whole page with health memories instead of burying them behind
  // unrelated docs.
  const categoryFilter:
    | { operation: string; value: unknown; type: string }
    | undefined =
    realSelectedCategories.length === 1
      ? { operation: "equal", value: realSelectedCategories[0], type: "NUMBER" }
      : realSelectedCategories.length > 1
      ? { operation: "in", value: realSelectedCategories, type: "NUMBER" }
      : selectedCategories.length > 0
      ? { operation: "in", value: [], type: "NUMBER" }
      : undefined;

  // Year filter state
  const yearParam = searchParams.get("year");
  const [yearDrawerOpen, setYearDrawerOpen] = React.useState(false);
  const [selectedYears, setSelectedYears] = React.useState<number[]>(() => {
    if (!yearParam) return [];
    return yearParam
      .split(",")
      .map(Number)
      .filter((v) => !isNaN(v));
  });

  // Seeded from localStorage for immediate paint; refreshed by the
  // memberId-keyed year-facet fetch below.
  const [availableYears, setAvailableYears] = React.useState<number[]>(() => {
    const stored = localStorage.getItem("documentListingAvailableYears");
    return stored ? JSON.parse(stored) : [];
  });

  // Sync selectedCategories with category param in URL
  React.useEffect(() => {
    if (!categoryParam) {
      setSelectedCategories([]);
    } else {
      const cats = categoryParam
        .split(",")
        .map(Number)
        .filter((v) => !isNaN(v));
      setSelectedCategories(cats);
    }
  }, [categoryParam]);

  // Track whether we've synced searchTerm from URL
  const [searchInitialized, setSearchInitialized] = React.useState(false);
  // Track if this is the first render
  const isFirstRender = React.useRef(true);
  // Track if we've already sent the tracking event
  const hasTrackedRef = React.useRef(false);

  // Reset everything when the active profile changes (component stays mounted).
  const prevMemberIdRef = React.useRef(memberId);
  // When true, the fetch effect skips its next run (stale-closure guard: the
  // first run after a profile switch closes over old filter state).
  const skipNextFetchRef = React.useRef(false);
  React.useEffect(() => {
    if (prevMemberIdRef.current === memberId) return;
    localStorage.removeItem(`documentFilters_${prevMemberIdRef.current}`);
    localStorage.removeItem("documentListingAvailableYears");
    skipNextFetchRef.current = true;
    prevMemberIdRef.current = memberId;
    // Clear filter state
    setSearchTerm("");
    setSearchInputValue("");
    setSelectedCategories([]);
    setSelectedYears([]);
    // Clear document data so old member's docs don't linger
    setDocuments([]);
    setAllDocuments([]);
    setAvailableYears([]);
    setPage(2);
    setHasMore(true);
    setTotalCount(0);
    // Clear URL params
    const next = new URLSearchParams();
    setSearchParams(next, { replace: true });
  }, [memberId]);

  // Track page load once
  React.useEffect(() => {
    if (!hasTrackedRef.current) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ALL_RECORDS,
      });
      hasTrackedRef.current = true;
    }
  }, []);

  // Restore filters from localStorage on mount.
  // Only the search term is restored — back-navigation strips ?search= from
  // the URL but leaves the user's intent intact. Category and year filters
  // are URL-driven exclusively: navigating to a URL without ?category= means
  // "no filter", so we must not re-inject a stale category from a prior visit.
  React.useEffect(() => {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);

      const urlSearch = searchParams.get("search");
      if (!urlSearch || urlSearch.trim() === "") {
        if (parsed.search) {
          setSearchTerm(parsed.search);
          searchParams.set("search", parsed.search);
          setSearchParams(searchParams, { replace: true });
        }
      }
    } catch (err) {
      console.error("Failed to parse saved filters:", err);
    }
  }, []);

  // Sync searchTerm from URL whenever it changes. First-run guard: when we
  // hydrated `searchTerm` from localStorage (because back-nav stripped the
  // URL), the very first run of this effect would otherwise overwrite the
  // hydrated value with the empty URL value. Skip that one case — the
  // filter-restore effect right above this one is queuing a `setSearchParams`
  // that will trigger a follow-up run with the correct URL.
  const urlSyncInitialisedRef = React.useRef(false);
  React.useEffect(() => {
    const search = searchParams.get("search") || "";
    const firstRun = !urlSyncInitialisedRef.current;
    urlSyncInitialisedRef.current = true;
    if (firstRun && search === "" && searchTerm !== "") {
      setSearchInputValue(searchTerm);
      setSearchInitialized(true);
      return;
    }
    setSearchTerm(search);
    setSearchInputValue(search);
    setSearchInitialized(true);
  }, [searchParams]);

  // Persist the search term to localStorage so back-navigation can recover it
  // (back-nav strips ?search= from the URL). Categories and years are not
  // persisted — they live in the URL and must not bleed into unrelated views.
  React.useEffect(() => {
    if (!searchInitialized) return;
    localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({ search: searchTerm })
    );
  }, [searchTerm, searchInitialized]);

  // Fetch the year facet independently of the rendered document list — it
  // must reflect the member's full date range regardless of which type/year
  // filter is currently active (or was carried in from a category card link
  // on the landing page, which leaves no prior "all records" fetch to derive
  // from). Keyed only on memberId, unfiltered by category/year/search.
  React.useEffect(() => {
    if (!memberId) return;
    let cancelled = false;

    async function fetchYearFacet() {
      try {
        const res = (await fetchDocuments({
          filters: {
            memberId: { operation: "EQUAL", value: memberId, type: "STRING" },
          },
          sort: [],
        })) as { data: any[] };
        if (cancelled) return;

        const data = Array.isArray(res?.data) ? res.data : [];
        const years = Array.from(
          new Set(
            data
              .map((d) => {
                if (
                  d.type === "healthMemory" &&
                  d.year &&
                  typeof d.year === "number"
                ) {
                  return d.year;
                }
                const date = d.documentDate ? new Date(d.documentDate) : null;
                return date ? date.getFullYear() : undefined;
              })
              .filter((y) => !!y)
          )
        ).sort((a, b) => b - a); // Sort descending

        setAvailableYears(years);
        localStorage.setItem(
          "documentListingAvailableYears",
          JSON.stringify(years)
        );
      } catch {
        // best-effort; year dropdown can fall back to whatever was cached
      }
    }

    fetchYearFacet();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  // Clear stored years when navigating to other document pages
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      // Check if we're navigating to a different document page
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/documents/list")) {
        localStorage.removeItem("documentListingAvailableYears");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  React.useEffect(() => {
    if (!searchInitialized) return;

    // Skip the first fetch after a profile switch — it closes over stale filter
    // state from the previous member. The state-clear triggers a second run
    // with clean values, which is the one that should actually fetch.
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    let timer: NodeJS.Timeout | null = null;

    async function fetchAll() {
      try {
        setLoading(true);
        const filters: any = {};

        if (categoryFilter) filters.category = categoryFilter;

        const yearFilter = buildYearFilter(selectedYears);
        if (yearFilter) {
          filters.documentDate = yearFilter;
        }

        filters.memberId = {
          operation: "EQUAL",
          value: memberId,
          type: "STRING",
        };

        const savedCount = getSavedItemCount();

        const docs = (await fetchDocuments({
          filters,
          sort: [{ key: "documentDate", direction: "desc" }],
          skipMemories: !includeHealthMemories,
          page: 1,
          pageSize: PAGE_SIZE,
        })) as { data: any[]; total?: number };
        const data = Array.isArray(docs.data) ? docs.data : [];
        let allData = await fetchAndMergeSettings(data);

        if (savedCount && savedCount > PAGE_SIZE) {
          let currentPage = 2;
          while (allData.length < savedCount) {
            const moreDocs = (await fetchDocuments({
              filters,
              sort: [{ key: "documentDate", direction: "desc" }],
              skipMemories: !includeHealthMemories,
              page: currentPage,
              pageSize: PAGE_SIZE,
            })) as { data: any[]; total?: number };
            const moreData = Array.isArray(moreDocs.data) ? moreDocs.data : [];
            if (moreData.length === 0) break; // No more data
            const moreDataWithSettings = await fetchAndMergeSettings(moreData);
            allData = [...allData, ...moreDataWithSettings];
            currentPage++;
            if (allData.length >= savedCount) break;
          }
          setPage(currentPage);
        } else {
          setPage(2);
        }

        setDocuments(allData);
        setAllDocuments(allData);
        setTotalCount(docs.total || data.length);
        setHasMore(
          allData.length % PAGE_SIZE === 0 &&
            allData.length < (docs.total || allData.length)
        );
      } catch (err) {
        console.error("Error fetching documents:", err);
      } finally {
        setLoading(false);
      }
    }

    async function fetchFuzzy() {
      setLoading(true);
      try {
        const filters: any = {};

        if (categoryFilter) filters.category = categoryFilter;

        if (selectedYears.length > 0) {
          filters.documentDate = {
            operation: "or_between",
            type: "DATE",
            value: selectedYears.map((year: number) => [
              new Date(year, 0, 1).toISOString(),
              new Date(year + 1, 0, 1).toISOString(),
            ]),
          };
        }

        const results = await vectorSearchDocuments(searchTerm, filters, {
          skipMemories: !includeHealthMemories,
        });
        setDocuments(Array.isArray(results) ? results : []);
        setTotalCount(results.length);
      } catch (err) {
        console.error("Error in fuzzy search:", err);
      } finally {
        setLoading(false);
      }
    }

    if (isSearchMode) {
      // VECTOR SEARCH ONLY
      if (isFirstRender.current) {
        fetchFuzzy();
      } else {
        timer = setTimeout(fetchFuzzy, 500);
      }
    } else {
      timer = setTimeout(fetchAll, 0);
    }

    isFirstRender.current = false;

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    searchTerm,
    selectedCategories,
    selectedYears,
    memberId,
    searchInitialized,
  ]);

  return (
    <div style={{ padding: "0 24px" }}>
      {/* Type filter dropdown — always shows the full fixed category order */}
      <MDropDownDrawer
        open={typeDrawerOpen}
        onClose={() => setTypeDrawerOpen(false)}
        title="Select type(s)"
        items={[
          ...CATEGORY_FILTER_ORDER.map((v) => ({
            value: String(v),
            label: getFileCategoryLabel(v),
          })),
          {
            value: String(HEALTH_MEMORY_FILTER_VALUE),
            label: "Health memory",
          },
        ]}
        variant="multi"
        selectedValues={selectedCategories.map(String)}
        onSelect={(vals) => {
          const arr = Array.isArray(vals) ? vals : [vals];
          // Remove empty/invalid
          const valid = arr.map(Number).filter((v) => !isNaN(v));
          if (valid.length === 0) {
            searchParams.delete("category");
          } else {
            searchParams.set("category", valid.join(","));
          }
          setSearchParams(searchParams, { replace: true });
        }}
      />
      {!selectMode && (
        <>
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              marginLeft: "-24px",
              marginRight: "-24px",
              paddingLeft: "24px",
              paddingRight: "24px",
            }}
          >
            <MHeader
              subHeading="Records"
              module="documents"
              memberInfo={memberInfo}
              showProfilePickerRow
              showGradient
              showSearch
              onSearch={handleSearch}
              searchPlaceholder="Search"
              value={searchInputValue}
              onClear={clearSearch}
              onValueChange={setSearchInputValue}
              onBack={handleBack}
              onAdd={() => setShowUploadOptions(true)}
              searchFilterBadge={{
                count: selectedCategories.length + selectedYears.length,
                onClear: handleResetFilters,
              }}
            />
          </div>
        </>
      )}
      {/* SELECT MODE UI */}
      {selectMode ? (
        <>
          {/* Top Bar */}
          <div
            className={styles.selectBar}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              background: "#FBFBFB",
            }}
          >
            <button
              className={styles.selectBarBack}
              onClick={() => {
                setSelectMode(false);
                setSelectedIds([]);
              }}
            >
              <span aria-label="Back">&#10005;</span>
            </button>
            <div className={styles.selectBarTitle}>Select records</div>
            <div style={{ width: 36 }} />
          </div>
          {/* Bulk actions: Favorites/Offline/Private pills (same style as the
           * shortcut chips elsewhere) + a circular delete button. */}
          <div className={styles.selectActionsRow}>
            <button
              type="button"
              className={`${styles.filterChip} ${
                selectedIds.length === 0 ? styles.filterChipDisabled : ""
              }`}
              style={{ ["--chip-accent" as string]: "#E3B341" }}
              disabled={selectedIds.length === 0}
              onClick={async () => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter((doc) =>
                  selectedIds.includes(doc._id || doc.id)
                );
                if (selectedDocs.length > 0) {
                  await bulkUpdateFlag(
                    selectedDocs,
                    "isImportant",
                    true,
                    setDocuments
                  );
                }
              }}
            >
              <IconStarFilled size={16} className={styles.filterChipIcon} />
              <span className={styles.filterChipLabel}>
                {t("documentsLanding.chips.favorites")}
              </span>
            </button>
            <button
              type="button"
              className={`${styles.filterChip} ${
                selectedIds.length === 0 ? styles.filterChipDisabled : ""
              }`}
              style={{ ["--chip-accent" as string]: "#468BE5" }}
              disabled={selectedIds.length === 0}
              onClick={async () => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter((doc) =>
                  selectedIds.includes(doc._id || doc.id)
                );
                if (selectedDocs.length > 0) {
                  await bulkUpdateFlag(
                    selectedDocs,
                    "availableOffline",
                    true,
                    setDocuments
                  );
                }
              }}
            >
              <IconCloudDownload size={16} className={styles.filterChipIcon} />
              <span className={styles.filterChipLabel}>
                {t("documentsLanding.chips.offline")}
              </span>
            </button>
            <button
              type="button"
              className={`${styles.filterChip} ${
                selectedIds.length === 0 ? styles.filterChipDisabled : ""
              }`}
              style={{ ["--chip-accent" as string]: "#217B57" }}
              disabled={selectedIds.length === 0}
              onClick={async () => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter((doc) =>
                  selectedIds.includes(doc._id || doc.id)
                );
                if (selectedDocs.length > 0) {
                  await bulkUpdateFlag(
                    selectedDocs,
                    "isPrivate",
                    true,
                    setDocuments
                  );
                }
              }}
            >
              <IconLockFilled size={16} className={styles.filterChipIcon} />
              <span className={styles.filterChipLabel}>
                {t("documentsLanding.chips.private")}
              </span>
            </button>
            <button
              type="button"
              className={`${styles.selectDeleteBtn} ${
                selectedIds.length === 0 ? styles.selectDeleteBtnDisabled : ""
              }`}
              disabled={selectedIds.length === 0}
              aria-label="Delete selected"
              onClick={() => {
                if (selectedIds.length === 0) return;
                const selectedDocs = documents.filter((doc) =>
                  selectedIds.includes(doc._id || doc.id)
                );
                if (selectedDocs.length > 0) {
                  setBulkDeleteDialogOpen(true);
                }
              }}
            >
              <IconTrash size={20} color="#983145" />
            </button>
            <MDialog
              isOpen={bulkDeleteDialogOpen}
              onClose={() => setBulkDeleteDialogOpen(false)}
              title={`Delete ${selectedIds.length} record${
                selectedIds.length > 1 ? "s" : ""
              }?`}
              description={
                selectedIds.length > 1
                  ? "Are you sure you want to delete these records? This action cannot be undone."
                  : "Are you sure you want to delete this record? This action cannot be undone."
              }
              primaryCtaText="Delete"
              destructivePrimary
              onPrimaryCta={async () => {
                const selectedDocs = documents.filter((doc) =>
                  selectedIds.includes(doc._id || doc.id)
                );
                await bulkDelete(selectedDocs, setDocuments);
                setSelectedIds([]);
                setSelectMode(false);
                setBulkDeleteDialogOpen(false);
              }}
              secondaryCtaText="Cancel"
              onSecondaryCta={() => setBulkDeleteDialogOpen(false)}
            />
          </div>
          {/* Selected count row */}
          <div className={styles.selectedCountRow}>
            <span>Selected {selectedIds.length} records</span>
            <MButton
              variant="link"
              size="sm"
              disabled={selectedIds.length === 0}
              onClick={() => {
                setSelectedIds([]);
              }}
            >
              Clear
            </MButton>
          </div>
          {/* Document Cards with checkboxes */}
          <div className={styles.documentCardsContainer}>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                  width: "100%",
                }}
              >
                <MLoader size={56} color="#4C189A" />
              </div>
            ) : (
              documents.map((doc, idx) => {
                const id = doc._id || doc.id || idx.toString();
                const checked = selectedIds.includes(id);
                return (
                  <MDocumentCard
                    cardType="document"
                    key={id}
                    category={doc.category}
                    displayName={doc.displayName || `Document ${idx + 1}`}
                    meta={
                      doc.documentDate
                        ? formatDocumentDateShort(doc.documentDate)
                        : ""
                    }
                    showComment={doc.notes}
                    isPrivate={doc.isPrivate}
                    isImportant={doc.isImportant || false}
                    availableOffline={doc.availableOffline || false}
                    insights={doc.insights || false}
                    selectMode={true}
                    selected={checked}
                    documentId={doc._id || doc.id}
                    showStatusIcons={true}
                    className={styles.documentListingCard}
                    onSelect={() => {
                      setSelectedIds((prev) =>
                        checked ? prev.filter((x) => x !== id) : [...prev, id]
                      );
                    }}
                    onViewDocument={() => {
                      saveScroll();
                      saveItemCount(documents.length);
                      if (doc.category === FileCategory.EmergencyCard) {
                        navigate("/dashboard/emergency-card", {
                          state: { backUrl: "/documents/list" },
                        });
                      } else {
                        navigate("/documents/list/view", {
                          state: {
                            document: doc,
                            backUrl: `${location.pathname}${location.search}`,
                          },
                        });
                      }
                    }}
                    onOptionsClick={() => {}}
                  />
                );
              })
            )}
          </div>
        </>
      ) : (
        <>
          {/* Main content area with white background */}
          <div
            style={{
              background: "#FFF",
              marginLeft: "-24px",
              marginRight: "-24px",
              padding: "0 24px",
              minHeight: "calc(100vh - 200px)",
            }}
          >
            <div className={styles.documentFilterRow}>
              <MFilter
                text="Filter by type"
                selected={selectedCategories.length > 0}
                onClick={() => setTypeDrawerOpen(true)}
                variant="sm"
              />
              <MFilter
                text="Filter by year"
                selected={selectedYears.length > 0}
                onClick={() => setYearDrawerOpen(true)}
                variant="sm"
              />
              <MDropDownDrawer
                open={yearDrawerOpen}
                onClose={() => setYearDrawerOpen(false)}
                title="Select year(s)"
                items={availableYears.map((y) => ({
                  value: String(y),
                  label: String(y),
                }))}
                variant="multi"
                selectedValues={selectedYears.map(String)}
                onSelect={(vals) => {
                  const arr = Array.isArray(vals) ? vals : [vals];
                  const valid = arr.map(Number).filter((v) => !isNaN(v));
                  // Only update state and URL if the selection actually changed
                  if (
                    valid.length !== selectedYears.length ||
                    valid.some((v, i) => v !== selectedYears[i])
                  ) {
                    setSelectedYears(valid);
                    if (valid.length === 0) {
                      searchParams.delete("year");
                    } else {
                      searchParams.set("year", valid.join(","));
                    }
                    setSearchParams(searchParams, { replace: true });
                  }
                  setYearDrawerOpen(false); // Close drawer after selection
                }}
                height={(() => {
                  const yearCount = availableYears.length;
                  // Dynamic height: minimum 35vh, maximum 60vh, based on number of years
                  // Increased per-item height and base for better visibility
                  const calculatedHeight = Math.min(
                    Math.max(yearCount * 50 + 120, window.innerHeight * 0.35),
                    window.innerHeight * 0.6
                  );
                  return `${calculatedHeight}px`;
                })()}
              />
              <button
                type="button"
                className={styles.resetButton}
                disabled={
                  selectedCategories.length === 0 &&
                  selectedYears.length === 0 &&
                  searchTerm.length === 0 &&
                  searchInputValue.length === 0
                }
                onClick={handleResetFilters}
              >
                <IconReload size={12} className={styles.resetIcon} />
                <span className={styles.resetTextSecondary}>Reset</span>
              </button>
            </div>
            {/* Selected filters as chips, 16px below the filter row */}
            {(selectedCategories.length > 0 || selectedYears.length > 0) && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  margin: "16px 0 0 0",
                  flexWrap: "wrap",
                }}
              >
                {selectedCategories.map((cat) => (
                  <MChip
                    key={`cat-${cat}`}
                    text={
                      cat === HEALTH_MEMORY_FILTER_VALUE
                        ? "Health memory"
                        : getFileCategoryLabel(cat)
                    }
                    variant="filtered"
                    selected
                    onRightIconClick={() => {
                      const updated = selectedCategories.filter(
                        (c) => c !== cat
                      );
                      if (updated.length === 0) {
                        searchParams.delete("category");
                      } else {
                        searchParams.set("category", updated.join(","));
                      }
                      setSearchParams(searchParams, { replace: true });
                    }}
                  />
                ))}
                {selectedYears.map((year) => (
                  <MChip
                    key={`year-${year}`}
                    text={String(year)}
                    variant="filtered"
                    selected
                    onRightIconClick={() => {
                      const updated = selectedYears.filter((y) => y !== year);
                      setSelectedYears(updated);
                      if (updated.length === 0) {
                        searchParams.delete("year");
                      } else {
                        searchParams.set("year", updated.join(","));
                      }
                      setSearchParams(searchParams, { replace: true });
                    }}
                  />
                ))}
              </div>
            )}
            {/* Showing count and Select button row */}
            {processedDocuments.length > 0 && (
              <div className={styles.documentCountRow}>
                <span className="body-base-semibold colour-text-caption-n400 whitespace-nowrap">
                  {selectedCategories.length > 0 ||
                  selectedYears.length > 0 ||
                  isSearchMode
                    ? `${totalCount} ${
                        totalCount === 1 ? "record" : "records"
                      } found`
                    : `${totalCount} ${
                        totalCount === 1 ? "record" : "records"
                      }`}
                </span>
                {processedDocuments.some(
                  (doc) => doc.type !== "healthMemory"
                ) && (
                  <MButton
                    className={styles.selectMButton}
                    variant="link"
                    size="md"
                    onClick={() =>
                      navigate(`/documents/list/select${location.search}`, {
                        state: { documents: processedDocuments },
                      })
                    }
                  >
                    Select
                  </MButton>
                )}
              </div>
            )}
            <div className={styles.documentCardsContainer}>
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                    width: "100%",
                  }}
                >
                  <MLoader size={56} color="#4C189A" />
                </div>
              ) : (
                <MInfiniteScroll
                  data={processedDocuments}
                  hasMore={hasMore}
                  isLoading={isLoading}
                  onLoadMore={async () => {
                    setIsLoading(true);
                    const filters: any = {};
                    if (categoryFilter) filters.category = categoryFilter;
                    const yearFilter = buildYearFilter(selectedYears);
                    if (yearFilter) {
                      filters.documentDate = yearFilter;
                    }
                    filters.memberId = {
                      operation: "EQUAL",
                      value: memberId,
                      type: "STRING",
                    };
                    if (isSearchMode) {
                      const result = await vectorSearchDocuments(
                        searchTerm,
                        {
                          ...filters,
                          page,
                          pageSize: PAGE_SIZE,
                        },
                        { skipMemories: !includeHealthMemories }
                      );

                      const newItems = Array.isArray(result) ? result : [];

                      setDocuments((prev) => [...prev, ...newItems]);
                      setTotalCount((prev) => prev + newItems.length);
                      setPage((prev) => prev + 1);
                      setHasMore(newItems.length === PAGE_SIZE);
                      setIsLoading(false);
                      return;
                    }
                    const docs = (await fetchDocuments({
                      filters,
                      sort: [{ key: "documentDate", direction: "desc" }],
                      skipMemories: !includeHealthMemories,
                      page,
                      pageSize: PAGE_SIZE,
                    })) as { data: any[]; total?: number };
                    const newDocs = Array.isArray(docs.data) ? docs.data : [];
                    const newDocsWithSettings = await fetchAndMergeSettings(
                      newDocs
                    );
                    setDocuments((prev) => [...prev, ...newDocsWithSettings]);
                    setAllDocuments((prev) => [
                      ...prev,
                      ...newDocsWithSettings,
                    ]);
                    // Total should stay the same across pages
                    if (docs.total !== undefined) {
                      setTotalCount(docs.total);
                    }
                    setPage((prev) => prev + 1);
                    setHasMore(newDocs.length === PAGE_SIZE);
                    setIsLoading(false);
                  }}
                  renderItem={(item, idx) =>
                    item.type === "healthMemory" ? (
                      <HealthMemoryCard
                        key={item.id || `hm-${idx}`}
                        title={item.title}
                        year={item.year}
                        optionsMenu={[
                          {
                            id: "edit",
                            label: t("common.edit"),
                            icon: <IconEdit />,
                            onClick: () => {
                              // Persist scroll + item count so returning from
                              // the edit screen restores the list position
                              // instead of jumping to the top (same as the
                              // document cards' onViewDocument).
                              saveScroll();
                              saveItemCount(documents.length);
                              navigate(
                                `/documents/health-memory/edit?mode=edit&id=${item.id}`,
                                {
                                  state: {
                                    healthMemory: item,
                                    backUrl: "/documents/list",
                                  },
                                }
                              );
                            },
                            className: "",
                          },
                          {
                            id: "delete",
                            label: t("common.delete"),
                            icon: <IconCircleX />,
                            onClick: () => handleHealthMemoryDelete(item),
                            className: "delete",
                          },
                        ]}
                      />
                    ) : (
                      <MDocumentCard
                        cardType="document"
                        key={item._id || item.id || idx}
                        category={item.category}
                        displayName={item.displayName || `Document ${idx + 1}`}
                        meta={
                          item.documentDate
                            ? formatDocumentDateShort(item.documentDate)
                            : ""
                        }
                        showComment={item.notes}
                        isPrivate={item.isPrivate}
                        isImportant={item.isImportant || false}
                        availableOffline={item.availableOffline || false}
                        insights={item.insights || false}
                        documentId={item._id || item.id}
                        showStatusIcons={true}
                        className={styles.documentListingCard}
                        onOptionsClick={() => {
                          setDrawerMode("document");
                          setSelectedDoc(item);
                        }}
                        onViewDocument={() => {
                          saveScroll();
                          saveItemCount(documents.length);
                          if (item.category === FileCategory.EmergencyCard) {
                            navigate("/dashboard/emergency-card", {
                              state: { backUrl: "/documents/list" },
                            });
                          } else {
                            navigate("/documents/list/view", {
                              state: {
                                document: item,
                                backUrl: `${location.pathname}${location.search}`,
                              },
                            });
                          }
                        }}
                      />
                    )
                  }
                />
              )}
              {processedDocuments.length === 0 && !loading && (
                <MEmptyCard
                  primaryTitle="No records found"
                  secondaryTitle="Try another keyword or adjust your filters"
                  icon={<IconFileSearch />}
                />
              )}
            </div>
          </div>
        </>
      )}

      {drawerMode === "document" && selectedDoc && (
        <DocumentDrawer
          open={true}
          onClose={() => setDrawerMode("none")}
          onUpdate={async () => {
            setDrawerMode("none"); // Close drawer first
            clearFiltersAndRefetch(false); // Just refetch, don't clear filters
          }}
          onDocumentChange={async (updatedDoc) => {
            // Fetch fresh settings for the updated document
            const docId = updatedDoc._id || updatedDoc.id;
            const freshSettings = await getSettings(docId).catch(() => null);

            const docWithSettings = {
              ...updatedDoc,
              isImportant: freshSettings?.isImportant || false,
              availableOffline: freshSettings?.availableOffline || false,
            };

            // Update the document in the documents array with fresh settings
            setDocuments((prev) =>
              prev.map((doc) =>
                (doc._id || doc.id) === docId ? docWithSettings : doc
              )
            );

            // Also update allDocuments
            setAllDocuments((prev) =>
              prev.map((doc) =>
                (doc._id || doc.id) === docId ? docWithSettings : doc
              )
            );

            // Also update selectedDoc so drawer shows updated values
            setSelectedDoc(docWithSettings);
          }}
          displayName={String(selectedDoc.displayName || "Document")}
          fileKey={selectedDoc.media?.fileKey}
          fileUrl={
            selectedDoc.media?.fileKey ? selectedDoc.media.fileKey : undefined
          }
          fileName={
            selectedDoc.media?.fileName || selectedDoc.displayName || "Document"
          }
          onDownloadClick={async () => {
            if (!selectedDoc.media?.fileKey) return;
            await downloadDocument(
              selectedDoc.media.fileKey,
              selectedDoc.media.fileName || "Document"
            );
          }}
          onDelete={async () => {
            await deleteDocument(selectedDoc, setDocuments);
            //toast.showToast({ message: 'Document deleted', variant: 'success' });
            setDrawerMode("none");
            window.location.reload();
          }}
          onCommentClick={() => setDrawerMode("comment")}
          commentButtonText={selectedDoc.notes ? "Edit Comment" : "Add Comment"}
          category={selectedDoc.category}
          document={selectedDoc}
        />
      )}
      {/* Health Memory delete confirmation dialog */}
      <MDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setHealthMemoryToDelete(null);
        }}
        title="Delete this memory?"
        description="This memory will be permanently removed from your health records."
        primaryCtaText="Yes"
        onPrimaryCta={async () => {
          if (!healthMemoryToDelete) return;
          await deleteHealthMemory(healthMemoryToDelete.id);
          setDocuments((docs) =>
            docs.filter((doc) => doc.id !== healthMemoryToDelete.id)
          );
          setShowDeleteDialog(false);
          setHealthMemoryToDelete(null);
          //toast.showToast({ message: 'The health memory note has been deleted.', variant: 'success' });
        }}
        secondaryCtaText="No"
        onSecondaryCta={() => {
          setShowDeleteDialog(false);
          setHealthMemoryToDelete(null);
        }}
      />
      {drawerMode === "comment" && selectedDoc && (
        <DocumentCommentDrawer
          open={true}
          onClose={() => setDrawerMode("none")}
          initialValue={selectedDoc.notes || ""}
          loading={commentLoading}
          onDone={async (notes) => {
            setCommentLoading(true);
            try {
              await updateDocumentNotes(selectedDoc, notes, setDocuments);
              //toast.showToast({ message: selectedDoc.notes ? 'Comment updated' : 'Comment added', variant: 'success' });
              setSelectedDoc((doc) => ({ ...doc, notes }));
              setDrawerMode("none");
            } finally {
              setCommentLoading(false);
            }
          }}
          title={`${selectedDoc.notes ? "Edit Comment" : "Add Comment"}`}
        />
      )}
      <MDocumentUploadOptions
        open={showUploadOptions}
        onClose={() => setShowUploadOptions(false)}
      />
    </div>
  );
};

export default DocumentListing;
